import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SettingsModalBoldText } from '../../settings/SettingsModals';
import { closeAddUserTypeModal } from '../ManageUserComponents';
import {
  ModalButtons,
  ModalHeader,
  StyledModal,
  StyledModalBody
} from '../../../components/Modals';
import 'antd/dist/antd.css';
import UnitTreeCheckBoxes from '../UnitTreeCheckBoxes';
import { openModal } from '../../../helper/modals';
import { updateUserTypeData } from '../../../redux/actions/addUserType';
import styled from 'styled-components';
import { Palette } from '../../../components/utils';
import { FaSearch } from 'react-icons/fa';
import {CInvalidFeedback} from '@coreui/react';
import {LockProviders} from '../../../utils/lockInfo';

const MAX_SALTO_LOCKS_SELECTION = 96;

const AddNewUserTypeModalStepAccessControl = () => {
  const { t } = useTranslation();
  const { modal, userId } = useSelector((state) => state.headerModal);
  const addUserType = useSelector((state) => state.addUserType);
  const dispatch = useDispatch();
  const { allBuildings } = useSelector((state) => state.building);
  const [selectedUnitIds, setSelectedUnitIds] = useState(addUserType.units || []);
  const [selectedSuiteIds, setSelectedSuiteIds] = useState(addUserType.suites || []);
  const [selectedDoorIds, setSelectedDoorIds] = useState(addUserType.doors || []);
  const [searchTerm, setSearchTerm] = useState('');

  const currentBuildingConfig = useMemo(() => {
    const currentBuilding = allBuildings?.find((b) => b.id === addUserType?.buildings?.buildingId);
    return currentBuilding?.config;
  }, [allBuildings, addUserType?.buildings?.buildingId]);

  // Handle Max Salto locks
  const {selectedDevicesNum, maxExceeded, displayCount} = useMemo(() => {
    const displayCount = [LockProviders.SALTO, LockProviders.SALTO_ALFRED].includes(currentBuildingConfig?.lock_provider);
    const selectedDevicesNum = selectedUnitIds.length + selectedSuiteIds.length + selectedDoorIds.length;
    const maxExceeded = selectedDevicesNum > MAX_SALTO_LOCKS_SELECTION;
    return {
      displayCount,
      selectedDevicesNum,
      maxExceeded
    };
  }, [selectedUnitIds, selectedSuiteIds, selectedDoorIds, currentBuildingConfig]);

  const onSelectionChange = (unitIds, suiteIds, doorsIds) => {
    setSelectedUnitIds(unitIds);
    setSelectedSuiteIds(suiteIds);
    setSelectedDoorIds(doorsIds);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <StyledModal
      show={modal === 'AddNewUserTypeModalStepAccessControl'}
      closeOnBackdrop={false}
      onClose={() => closeAddUserTypeModal(addUserType.editingID)}>
      <ModalHeader
        title={t('Access Control')}
        onCloseCallback={() => closeAddUserTypeModal(addUserType.editingID)}
      />

      <StyledModalBody gap="0px">
        {addUserType?.buildings?.buildingId && currentBuildingConfig ? (
          <>
            {
              (!addUserType?.saltoAdded && [LockProviders.SALTO, LockProviders.SALTO_ALFRED].includes(currentBuildingConfig?.lock_provider)) && (
                <CInvalidFeedback 
                  style={{ 
                    display: 'block',
                    fontSize: '16px',
                    marginBottom: '16px',
                    marginLeft: '10px'
                  }}
                >
                  {t("The user has not yet been added to Salto. Access will be granted once the user’s credentials are issued!")}
                </CInvalidFeedback>
              )
            }
            <FilterContainer>
              <SearchIcon />
              <SearchInput
                placeholder={t('Search units or suites...')}
                value={searchTerm}
                onChange={handleSearch}
              />
            </FilterContainer>
            {displayCount && (
              <>
              <CounterText isLimitReached={maxExceeded}>
                {`${selectedDevicesNum} / ${MAX_SALTO_LOCKS_SELECTION}`}
              </CounterText>

              {
                (displayCount && maxExceeded) && (
                  <CInvalidFeedback 
                    style={{ 
                      display: 'block',
                      fontSize: '16px',
                      marginBottom: '10px',
                      textAlign: 'center'
                    }}
                  >
                    {t("max_vendor_access", { limit: MAX_SALTO_LOCKS_SELECTION })}
                  </CInvalidFeedback>
                )
              }
              </>
            )}
            <CheckboxContainer>
              <UnitTreeCheckBoxes
                buildingId={addUserType.buildings.buildingId}
                organizationId={addUserType?.organization?.organizationId}
                onSelectionChange={onSelectionChange}
                initialSelectedUnitIds={selectedUnitIds}
                initialSelectedSuiteIds={selectedSuiteIds}
                initialSelectedDoorIds={selectedDoorIds}
                suiteEnabled={currentBuildingConfig.suite_enabled}
                buidlingConfig={currentBuildingConfig}
                showOnlyUnitsWithLock={true}
                searchTerm={searchTerm}
              />
            </CheckboxContainer>
          </>
        ) : (
          <p>{t('Please select a building first.')}</p>
        )}
      </StyledModalBody>
      <ModalButtons
        leftButtonText={t('Back')}
        leftButtonClick={() => openModal('AddNewUserTypeModalStep2')}
        rightButtonText={t('Next')}
        rightDisabled={displayCount && maxExceeded}
        rightButtonClick={() => {
          dispatch(
            updateUserTypeData({
              units: selectedUnitIds,
              suites: selectedSuiteIds,
              doors: selectedDoorIds
            })
          );
          openModal('AddNewUserTypeModalStep3');
        }}
      />
    </StyledModal>
  );
};

const FilterContainer = styled.div`
    position: sticky;
    top: 0;
    background: ${Palette.WHITE};
    z-index: 10; 
    padding: 0; 
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 8px 12px 8px 35px;
    border: 1px solid ${Palette.GRAY_30};
    border-radius: 10px;
    font-size: 14px;
    outline: none;
    &:focus {
        border-color: ${Palette.BLUE_60};
        box-shadow: 0 0 3px rgba(24, 144, 255, 0.2);
    }
    &::placeholder {
        color: ${Palette.GRAY_50};
    }
`;

const SearchIcon = styled(FaSearch)`
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${Palette.GRAY_50};
    font-size: 16px;
`;

const CheckboxContainer = styled.div`
    margin-top: 10px;
    max-height: 400px;
    overflow-y: auto;
`;

const CounterText = styled.p`
  display: block;
  font-size: 16px;
  margin-top: 16px;
  margin-bottom: 5px;
  text-align: center;
  font-weight: 600;
  color: ${props => props.isLimitReached ? Palette.ERROR : Palette.BLACK}; 
  transition: color 0.2s ease-in-out;
`;

export default AddNewUserTypeModalStepAccessControl;